import { Injectable } from '@angular/core';
import { DocumentTypesEnum } from '../document-data/document-data.data';
import { Banner } from '../features-data/sub-models/banner.model';
import { Canvas } from '../features-data/sub-models/canvas.model';
import { AccountTrackingObject } from './models/objects/account-tracking-object.model';
import { AddressTrackingObject } from './models/objects/address-tracking-object.model';
import { AutoListTrackingObject } from './models/objects/auto-list-tracking-object.model';
import { BannerTrackingObject } from './models/objects/banner-tracking-object.model';
import { CanvasTrackingObject } from './models/objects/canvas-tracking-object.model';
import { ClientTrackingObject } from './models/objects/client-tracking-object.model';
import { DialogTrackingObject } from './models/objects/dialog-tracking-object.model';
import { DocumentListTrackingObject } from './models/objects/document-list-tracking-object.model';
import { DocumentTrackingObject } from './models/objects/document-tracking-object.model';
import { GlobalSearchTrackingObject } from './models/objects/global-search-tracking-object.model';
import { GrowthCanvasTrackingObject } from './models/objects/growth-canvas-tracking-object.model';
import { HomeScreenTrackingObject } from './models/objects/home-screen-tracking-object.model';
import { InputListTrackingObject } from './models/objects/input-list-tracking-object.model';
import { InputTrackingObject } from './models/objects/input-tracking-object.model';
import { ManageAutorenewTrackingObject } from './models/objects/manage-autorenew-tracking-object.model';
import { NavigationTrackingObject } from './models/objects/navigation-tracking-object.model';
import { OnboardingTrackingObject } from './models/objects/onboarding-tracking-object.model';
import { PopupTrackingObject } from './models/objects/popup-tracking-object.model';
import { ReportTrackingObject } from './models/objects/report-tracking-object.model';
import { SettingsTrackingObject } from './models/objects/settings-tracking-object.model';
import { StatementTrackingObject } from './models/objects/statement-tracking-object.model';
import { TemplateEditorTrackingObject } from './models/objects/template-editor-tracking-object.model';
import {
  ITrackingErrorProperties,
  ITrackingObject,
  ITrackingProperties,
  TrackingCanvasCategoryEnum,
  TrackingCanvasTypeEnum,
  TrackingDialogNamesEnum,
  TrackingInputTypeValuesEnum,
  TrackingObjectDocumentTypesEnum,
  TrackingObjectNamesEnum,
} from './tracking.data';
import { RafTrackingObject } from './models/objects/raf-tracking-object.model';
import { PromoBannerTrackingObject } from './models/objects/promo-banner-tracking-object.model';
import { NotificationTrackingObject } from './models/objects/notification-tracking-object.model';
import { NotificationStateEnum } from '../notification-data/notification.model';
import { SurveyTrackingObject } from './models/objects/survey-tracking-object.model';
import { PerformanceMetricTrackingObject } from './models/objects/performance-metric-tracking-object.model';
import { RenewalInfo } from '../billing-data/models/renewal-info.model';
import { UnmatchedPayment } from '../invoice-match-payments-data/models/unmatched-payment.model';
import { UnmatchedPaymentTrackingObject } from './models/objects/unmatched-payment-tracking-object.model';
import { MatchPaymentTrackingObject } from './models/objects/match-payment-tracking-object.model';
import { UnmatchedPaymentBannerTrackingObject } from './models/objects/unmatched-payment-banner-tracking-object.model';
import { TaskTrackingObject } from './models/objects/task-tracking-object.model';
import { TaskCompletionTrackingObject } from './models/objects/task-completion-tracking-object.model';
import { TaskItem } from '../home-data/models/submodels/task-item.model';
import { FeedbackTrackingObject } from './models/objects/feedback-tracking-object.model';
import { NetworkTrackingObject } from './models/objects/network-tracking-object.model';
import { OfferTypesEnum } from '../pricing-data/pricing-data.data';
import { OfferTrackingObject } from './models/objects/offer-tracking-object.model';
import { HelpTrackingObject } from './models/objects/help-object.model';
import { ProfileReadReviewsTrackingObject } from './models/objects/profile-read-review-tracking-object.model';
import { ProfileRequestReviewsTrackingObject } from './models/objects/profile-request-review-tracking-object.model';
import { AlertBannerTrackingObject } from './models/objects/alert-banner-tracking-object.model';
import { EmailVerificationLockTrackingObject } from './models/objects/email-verification-lock-object.model';
import { ProfileReadReviewsDetailTrackingObject } from './models/objects/profile-read-review-detail-tracking-object.model';
import { JobTrackingObject } from './models/objects/job-tracking-object.model';
import { ReportExpandedViewTypesEnum } from '../report-data/report-data.data';
import { ReportExpandedTrackingObject } from './models/objects/report-expanded-module.model';

export const buildDialogTrackingObject = (
  dialog_identifier: string
): ITrackingObject => ({
  name: 'dialog',
  properties: { dialog_identifier },
});

export const buildOptOutTrialCanvasTrackingObject = (
  properties?: ITrackingProperties
): ITrackingObject => ({
  name: 'opt_out_trial_landing_page',
  properties,
});

@Injectable()
export class TrackingObjectBuilder {
  buildGuidedTour(identifier: string) {
    const properties = { guided_tour_identifier: identifier };
    return { ...properties, properties, name: 'guided_tour' };
  }

  buildTopNavigation(): ITrackingObject {
    return { name: TrackingObjectNamesEnum.top_navigation, properties: {} };
  }

  buildNotificationsV2(): ITrackingObject {
    return { name: TrackingObjectNamesEnum.notifications, properties: {} };
  }

  buildEmailVerificationLock(): EmailVerificationLockTrackingObject {
    return new EmailVerificationLockTrackingObject();
  }

  buildAccount(
    email: string,
    accountId: string,
    userId: string
  ): AccountTrackingObject {
    const trackingKeys = AccountTrackingObject.KEYS;

    return new AccountTrackingObject({
      [trackingKeys.EMAIL]: email,
      [trackingKeys.ACCOUNT_ID]: accountId,
      [trackingKeys.I2G_USER_ID]: userId,
    });
  }

  buildAddress(): AddressTrackingObject {
    return new AddressTrackingObject();
  }

  buildAutoList(): AutoListTrackingObject {
    return new AutoListTrackingObject();
  }

  buildBanner(banner: Banner): BannerTrackingObject {
    return new BannerTrackingObject(banner.tracking);
  }

  buildAlertBanner(identifier?: string): AlertBannerTrackingObject {
    return new AlertBannerTrackingObject({ identifier });
  }

  buildCanvas(
    identifier: string,
    type: TrackingCanvasTypeEnum,
    category: TrackingCanvasCategoryEnum,
    cta1?: string,
    cta2?: string
  ): CanvasTrackingObject {
    const trackingKeys = CanvasTrackingObject.KEYS;

    return new CanvasTrackingObject({
      [trackingKeys.CANVAS_IDENTIFIER]: identifier,
      [trackingKeys.CANVAS_TYPE]: type,
      [trackingKeys.CANVAS_CATEGORY]: category,
      [trackingKeys.CTA1_BUTTON_IDENTIFIER]: cta1,
      [trackingKeys.CTA2_BUTTON_IDENTIFIER]: cta2,
    });
  }

  buildClient(clientId: string, clientLocalId: string): ClientTrackingObject {
    const trackingKeys = ClientTrackingObject.KEYS;

    return new ClientTrackingObject({
      [trackingKeys.ID]: clientId,
      [trackingKeys.LOCAL_ID]: clientLocalId,
    });
  }

  buildDialog(identifier: TrackingDialogNamesEnum): DialogTrackingObject {
    const trackingKeys = DialogTrackingObject.KEYS;

    return new DialogTrackingObject({
      [trackingKeys.DIALOG_IDENTIFIER]: identifier,
    });
  }

  buildDrawer(identifier: string): ITrackingObject {
    return {
      name: TrackingObjectNamesEnum.drawer,
      properties: {
        drawer_identifer: identifier,
      },
    };
  }

  buildFile(fileType: string): ITrackingObject {
    return {
      name: TrackingObjectNamesEnum.file,
      properties: {
        file_type: fileType,
      },
    };
  }

  buildDocument(
    documentId: string,
    localId: string,
    documentNumber: string,
    docStatus: string,
    revisionId: string,
    docType: DocumentTypesEnum
  ): DocumentTrackingObject {
    const trackingKeys = DocumentTrackingObject.KEYS;

    return new DocumentTrackingObject({
      [trackingKeys.DOCUMENT_ID]: documentId,
      [trackingKeys.DOCUMENT_LOCAL_ID]: localId,
      [trackingKeys.DOCUMENT_NUMBER]: documentNumber,
      [trackingKeys.DOCUMENT_STATUS]: docStatus,
      [trackingKeys.DOCUMENT_REVISION_ID]: revisionId,
      [trackingKeys.DOCUMENT_TYPE]: docType,
    });
  }

  buildDocumentList(
    documentType: TrackingObjectDocumentTypesEnum
  ): DocumentListTrackingObject {
    const trackingKeys = DocumentListTrackingObject.KEYS;

    return new DocumentListTrackingObject({
      [trackingKeys.DOCUMENT_TYPE]: documentType,
    });
  }

  buildGrowthCanvas(canvas: Canvas): GrowthCanvasTrackingObject {
    return new GrowthCanvasTrackingObject(canvas.tracking);
  }

  buildGlobalSearch(): GlobalSearchTrackingObject {
    return new GlobalSearchTrackingObject();
  }

  buildHomeScreen(): HomeScreenTrackingObject {
    return new HomeScreenTrackingObject();
  }

  buildInputList(
    inputType: TrackingInputTypeValuesEnum
  ): InputListTrackingObject {
    const trackingKeys = InputListTrackingObject.KEYS;

    return new InputListTrackingObject({
      [trackingKeys.INPUT_TYPE]: inputType,
    });
  }

  buildInput(
    inputId: string,
    localId: string,
    inputType: TrackingInputTypeValuesEnum
  ): InputTrackingObject {
    const trackingKeys = InputTrackingObject.KEYS;

    return new InputTrackingObject({
      [trackingKeys.INPUT_ID]: inputId,
      [trackingKeys.INPUT_LOCAL_ID]: localId,
      [trackingKeys.INPUT_TYPE]: inputType,
    });
  }

  buildManageAutorenew(
    renewalInfo: RenewalInfo,
    promoCode: string
  ): ManageAutorenewTrackingObject {
    const trackingKeys = ManageAutorenewTrackingObject.KEYS;
    const subscription = renewalInfo && renewalInfo.subscription;

    return new ManageAutorenewTrackingObject({
      [trackingKeys.CURRENT_PLAN_UUID]: subscription && subscription.planUuid,
      [trackingKeys.PURCHASE_WINDOW]:
        subscription && subscription.purchaseWindow,
      [trackingKeys.PROMO_CODE]: promoCode,
      [trackingKeys.AUTO_RENEW_ENABLED]:
        renewalInfo && renewalInfo.braintreeWillAutorenew,
    });
  }

  buildNavigation(): NavigationTrackingObject {
    return new NavigationTrackingObject();
  }

  buildOnboarding(): OnboardingTrackingObject {
    const trackingKeys = OnboardingTrackingObject.KEYS;

    return new OnboardingTrackingObject({
      [trackingKeys.NEW_USER]: true,
      [trackingKeys.ACCOUNT_TEMPLATE_TYPE]: null, // Always null for new user onboarding
      [trackingKeys.ACCOUNT_OWNER]: true,
    });
  }

  buildPopup(identifier: string): PopupTrackingObject {
    const trackingKeys = PopupTrackingObject.KEYS;

    return new PopupTrackingObject({
      [trackingKeys.IDENTIFIER]: identifier,
    });
  }

  buildPromoBanner(identifier: string): PromoBannerTrackingObject {
    const trackingKeys = PromoBannerTrackingObject.KEYS;

    return new PromoBannerTrackingObject({
      [trackingKeys.IDENTIFIER]: identifier,
    });
  }

  buildRaf(): RafTrackingObject {
    return new RafTrackingObject();
  }

  buildReport(): ReportTrackingObject {
    return new ReportTrackingObject();
  }

  buildHelp(): HelpTrackingObject {
    return new HelpTrackingObject();
  }

  buildStatement(
    clientId: string,
    statementViewId: string,
    dateStart: string,
    dateEnd: string,
    documentIds: string[],
    revisionIds: string[]
  ): StatementTrackingObject {
    const trackingKeys = StatementTrackingObject.KEYS;

    return new StatementTrackingObject({
      [trackingKeys.CLIENT_ID]: clientId,
      [trackingKeys.STATEMENT_VIEW_ID]: statementViewId,
      [trackingKeys.DATE_RANGE_START]: dateStart,
      [trackingKeys.DATE_RANGE_END]: dateEnd,
      [trackingKeys.DOCUMENT_ID_ARRAY]: documentIds,
      [trackingKeys.REVISION_ID_ARRAY]: revisionIds,
    });
  }

  buildSurvey(identifier: string): SurveyTrackingObject {
    const trackingKeys = SurveyTrackingObject.KEYS;

    return new SurveyTrackingObject({
      [trackingKeys.IDENTIFIER]: identifier,
    });
  }

  buildSettings(): SettingsTrackingObject {
    return new SettingsTrackingObject();
  }

  buildTemplateEditor(): TemplateEditorTrackingObject {
    return new TemplateEditorTrackingObject();
  }

  buildNotification(
    state: string,
    actionUrl: string
  ): NotificationTrackingObject {
    const trackingKeys = NotificationTrackingObject.KEYS;

    let translatedState = null;
    switch (state) {
      case NotificationStateEnum.dismissed:
      case NotificationStateEnum.read:
      case NotificationStateEnum.seen:
        translatedState = 'read';
        break;
      case NotificationStateEnum.new:
        translatedState = 'unread';
        break;
    }

    return new NotificationTrackingObject({
      [trackingKeys.STATE]: translatedState,
      [trackingKeys.ACTION_URL]: actionUrl,
    });
  }

  buildPerformanceMetric(): PerformanceMetricTrackingObject {
    return new PerformanceMetricTrackingObject();
  }

  buildUnmatchedPayment(
    payment: UnmatchedPayment
  ): UnmatchedPaymentTrackingObject {
    const trackingKeys = UnmatchedPaymentTrackingObject.KEYS;

    return new UnmatchedPaymentTrackingObject({
      [trackingKeys.TRANSACTION_ID]: payment.transactionGuid,
    });
  }

  buildMatchPayment(payment: UnmatchedPayment): MatchPaymentTrackingObject {
    const trackingKeys = MatchPaymentTrackingObject.KEYS;

    return new MatchPaymentTrackingObject({
      [trackingKeys.PAYMENT_AMOUNT]: payment.unmatchedAmount,
      [trackingKeys.PAYMENT_DATE]: payment.createdTimestamp,
      [trackingKeys.CLIENT_ID]: payment.clientId,
      [trackingKeys.TRANSACTION_ID]: payment.transactionGuid,
      [trackingKeys.MATCH_TYPE]: 'ach_credit',
    });
  }

  buildUnmatchedPaymentBanner(
    numOfUnmatchedPayments: number
  ): UnmatchedPaymentBannerTrackingObject {
    const trackingKeys = UnmatchedPaymentBannerTrackingObject.KEYS;

    return new UnmatchedPaymentBannerTrackingObject({
      [trackingKeys.PAYMENT_NUMBER]: numOfUnmatchedPayments,
    });
  }

  buildTask(taskItem: TaskItem): TaskTrackingObject {
    const trackingKeys = TaskTrackingObject.KEYS;

    return new TaskTrackingObject({
      [trackingKeys.LIST_IDENTIFIER]: taskItem.type,
      [trackingKeys.LIST_STATE]: taskItem.state,
    });
  }

  buildTaskCompletion(): TaskCompletionTrackingObject {
    return new TaskCompletionTrackingObject();
  }

  buildFeedback(): FeedbackTrackingObject {
    return new FeedbackTrackingObject();
  }

  buildNetwork(
    properties: ITrackingErrorProperties
  ): MatchPaymentTrackingObject {
    return new NetworkTrackingObject(properties);
  }

  buildOffer(identifier: OfferTypesEnum): OfferTrackingObject {
    const offerTrackingObjectKeys = OfferTrackingObject.KEYS;

    return new OfferTrackingObject({
      [offerTrackingObjectKeys.IDENTIFIER]: identifier,
    });
  }

  buildProfileReadReviews() {
    return new ProfileReadReviewsTrackingObject();
  }

  buildProfileReadReviewsDetail() {
    return new ProfileReadReviewsDetailTrackingObject();
  }

  /**
   * @param clientIds - client ids in comma separated string
   */
  buildProfileRequestReviews(clientIds: string) {
    const trackingKeys = ProfileRequestReviewsTrackingObject.KEYS;

    return new ProfileRequestReviewsTrackingObject({
      [trackingKeys.CLIENT_IDS]: clientIds,
    });
  }

  buildJob(clientId: string) {
    const trackingKeys = JobTrackingObject.KEYS;

    return new JobTrackingObject({
      [trackingKeys.ID]: clientId,
    });
  }

  buildReportExpanded(
    reportExpandedIdentifier: ReportExpandedViewTypesEnum
  ): ReportExpandedTrackingObject {
    const trackingKeys = ReportExpandedTrackingObject.KEYS;

    return new ReportExpandedTrackingObject({
      [trackingKeys.REPORT_EXPANDED_IDENTIFIER]: reportExpandedIdentifier,
    });
  }

  buildInstantWebsite() {
    return {
      name: TrackingObjectNamesEnum.instant_website,
      properties: {},
    };
  }
}
